import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import MuiDrawer from "@mui/material/Drawer";
import CssBaseline from "@mui/material/CssBaseline";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Collapse from "@mui/material/Collapse";
import {
  sideMenuItems,
  adminMenuItems,
  logoutMenuItems,
  profileMenuItems,
  userMenuItems
} from "../../data/SideMenuList";
import "../../styles/ListingPageFilter.css"
import { getStorageValue, hasChildren, deleteAllStorageValue } from "../../utils/helper";
import { Box, Typography, Modal, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../utils/AuthContext";
import { ReactComponent as UpArrow } from "../../assets/logos/up_arrow.svg";
import { ReactComponent as DownArrow } from "../../assets/logos/down_arrow.svg";
import NotificationsListAPI from "../../utils/services/notifications.api";
import { ReactComponent as AdminActive } from "../../assets/logos/Admin_active.svg";
import { ReactComponent as UsersActive } from "../../assets/logos/users_active.svg";
import { ReactComponent as NotificationActive } from "../../assets/logos/notification_active.svg";
import { ReactComponent as MyRequestActive } from "../../assets/logos/my_request_active.svg";
import { ReactComponent as MyDeliveryActive } from "../../assets/logos/my_delivery_active.svg";
import { ReactComponent as AdminProfileActive } from "../../assets/logos/profile_with_circle.svg";
import { ReactComponent as Admin } from "../../assets/logos/Admin.svg";
import { ReactComponent as Users } from "../../assets/logos/users.svg";
import { ReactComponent as Notification } from "../../assets/logos/notification.svg";
import { ReactComponent as MyRequest } from "../../assets/logos/my_request.svg";
import { ReactComponent as MyDelivery } from "../../assets/logos/my_delivery.svg";
import { ReactComponent as Logout } from "../../assets/logos/logout.svg";
import { ReactComponent as MyPackages } from "../../assets/logos/my_packages.svg";
import { ReactComponent as NotifyIcon } from "../../assets/logos/bell.svg";
import ConfirmationPopup from "./ConfirmationPopup";

const drawerWidth = 200;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(8.5)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8.5)} + 1px)`,
  },
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  height: "100vh",
  zIndex: 1200,
  position: "fixed",

  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": {
      ...openedMixin(theme),
      position: "fixed",
    },
  }),

  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": {
      ...closedMixin(theme),
      position: "fixed",
    },
  }),

  [theme.breakpoints.down("sm")]: {
    position: "fixed",
    height: "100vh",
    zIndex: 1200,
  },
}));

function SideMenu({ showNotifications }) {
  const navigate = useNavigate();
  const iconList = {
    "Admin": <Admin />,
    "My Delivery": <MyDelivery />,
    "My Request": <MyRequest />,
    "Users": <Users />,
    "Notifications": <Notification />,
    "Admin Profile": <AdminProfileActive />,
    "Logout": <Logout width={32} />,
    "My Packages": <MyPackages />,
    "My Profile": <AdminProfileActive />,
  };
  const activeIconList = {
    "Admin": <AdminActive />,
    "My Delivery": <MyDeliveryActive />,
    "My Request": <MyRequestActive />,
    "Users": <UsersActive />,
    "Notifications": <NotificationActive />,
    "Admin Profile": <AdminProfileActive />,
    "My Profile": <AdminProfileActive />,
  };
  const { logout } = useAuth();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const { disableFirebaseToken } = NotificationsListAPI;
  const userId = getStorageValue("userId");
  const roleId = getStorageValue("roleId") ? Number(getStorageValue("roleId")) : "";

  const [open, setOpen] = useState(localStorage.getItem("openMenu") === "yes" ? true : false);
  const [openCancel, setOpenCancel] = useState(false);
  const [url, setUrl] = useState("");

  const MenuItem = ({ item }) => {
    const Component = hasChildren(item) ? MultiLevel : SingleLevel;
    return <Component item={item} />;
  };

  const handleNavigation = (item) => {
    if(!getStorageValue("updatePhase")){
      deleteAllStorageValue("PageNumber", "PageSize", "SearchParams", "FilterParams", "SortByParams");
      if (item?.title === "Logout") {
        setConfirmDialogOpen(true);
      } else {
        if (
          // eslint-disable-next-line
          window.location.pathname == "/editProfile" ||
          // eslint-disable-next-line
          window.location.pathname == "/create-package" ||
          window.location.pathname.includes("/edit-package")
        ) {
          setUrl(item?.url);
          setOpenCancel(true);
        } else {
          navigate(item?.url);
        }
        localStorage.setItem("menuName", item?.title);
      }
      if (item?.title === "Notifications") {
        localStorage.removeItem("notifications");
        localStorage.removeItem("isReload");
        localStorage.setItem("isClicked", true);
      }
    }
  };

  const handleCancelLogout = () => {
    setConfirmDialogOpen(false);
  };

  const handleLogoutSubmit = () => {
    disableFirebaseToken(userId);
    deleteAllStorageValue("lastName", "userInfo", "accessToken", "refreshToken", "userEmail", "roleId", 
      "userId", "firstName", "menuName", "openMenu", "SearchParams", "FilterParams", "PageSize", 
      "SortByParams", "RequestTabs", "DeliveryTabs", "SearchParamsChanged", "package:tab");
    logout();
    handleCancelLogout();
    navigate("/");
  };

  const handleCancel = () => setOpenCancel(false);

  const handleCancelConfirm = () => {
    navigate(url);
    setOpenCancel(false);
  };

  const SingleLevel = ({ item }) => {
    return (
      <ListItem
        sx={{
          marginLeft: "1rem",
          cursor: "pointer",
          border: (localStorage.getItem("menuName")?.includes("Profile") && item?.title?.includes("Profile")) ? "1px solid #E6EDFF" : "",
          borderRadius: (localStorage.getItem("menuName")?.includes("Profile") && item?.title?.includes("Profile")) ? "5px" : "",
          marginBottom: item?.title?.includes("Profile") ? "7px" : "",
        }}
        button
        onClick={() => handleNavigation(item)}
      >
        {localStorage.getItem("menuName") === item?.title
          ? activeIconList[item?.title]
          : item?.title !== "Notifications"
          ? iconList[item?.title]
          : localStorage.getItem("notifications") === "true" &&
            item?.title === "Notifications"
          ? <NotifyIcon />
          : iconList[item?.title]}
        <Typography
          sx={{
            color:
              localStorage.getItem("menuName") === item?.title
                ? "#0C68F4"
                : "#7C8DB5",
            fontSize: "14px",
            paddingLeft: "10px",
          }}
        >
          {item?.title}
        </Typography>
      </ListItem>
    );
  };

  const MultiLevel = ({ item }) => {
    const { items: children } = item;

    const handleClick = () => {
      localStorage.setItem("openMenu", localStorage.getItem("openMenu") === "yes" ? "no" : "yes");
      setOpen(!open)
    };

    return (
      <>
        <ListItem
          sx={{ marginLeft: "1rem", cursor: "pointer" }}
          button
          onClick={() => handleClick()}
        >
          {item.icon}
          <Typography
            sx={{ color: "#7C8DB5", paddingLeft: "10px", fontSize: "14px" }}
          >
            {item?.title}
          </Typography>
          {open ? <DownArrow /> : <UpArrow />}
        </ListItem>
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List sx={{ marginLeft: "2rem" }} component="div" disablePadding>
            {children.map((child, key) => (
              <MenuItem key={key} item={child} />
            ))}
          </List>
        </Collapse>
      </>
    );
  };

  return (
    <>
      <CssBaseline />
      <Drawer variant="permanent" open>
        <div
          style={{
            height: "46px",
            width: "71px",
            marginLeft: "2rem",
            marginTop: "2rem",
          }}
        >
          <img
            src={require("../../assets/logos/a2b-logo.svg").default}
            alt="mySvgImage"
            width="71px"
            height="46px"
            onDragStart={(e) => {
              e.preventDefault();
            }}
          />
        </div>
        <List sx={{ marginTop: "1rem" }}>
          {roleId === 1 ? sideMenuItems.map((item, key) => (
            <Box>
              <MenuItem key={key} item={item} />
            </Box>
          )) : userMenuItems.map((item, key) => (
            <Box>
              <MenuItem key={key} item={item} />
            </Box>
          ))}
        </List>
        <List className="sidemenu-bottom">
          <SingleLevel item={roleId === 1 ? adminMenuItems : profileMenuItems} />
          <SingleLevel item={logoutMenuItems} />
        </List>
        <Modal open={confirmDialogOpen} onClose={handleCancelLogout}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              backgroundColor: "white",
              padding: 4,
              borderRadius: 2,
              width: "350px",
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                backgroundColor: "#ffffff",
                textAlign: "center",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  marginBottom: "16px",
                }}
              >
                Logout
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  marginBottom: "24px",
                  color: "#555555",
                }}
              >
                Are you sure you want to log out?
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Button
                  onClick={handleCancelLogout}
                  variant="outlined"
                  sx={{
                    flex: 1,
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: "bold",
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleLogoutSubmit}
                  sx={{
                    flex: 1,
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: "bold",
                    backgroundColor: "#007BFF",
                    color: "#FFFFFF",
                    "&:hover": { backgroundColor: "#0056b3" },
                  }}
                >
                  Yes, Logout
                </Button>
              </Box>
            </Box>
          </Box>
        </Modal>
        <ConfirmationPopup
          open={openCancel}
          onClose={handleCancel}
          onConfirm={handleCancelConfirm}
          title="Cancel Confirmation"
          content="Are you sure you want to cancel? All unsaved changes will be lost."
        />
      </Drawer>
    </>
  );
}

export default SideMenu;
